export class AmbientSynth {
  private ctx: AudioContext;
  private output: GainNode;
  private isPlaying = false;

  // Oscillators
  private baseOsc: OscillatorNode | null = null;
  private harmonic2: OscillatorNode | null = null;
  private harmonic3: OscillatorNode | null = null;
  private lfoOsc: OscillatorNode | null = null;

  // Gains
  private masterGain: GainNode | null = null;
  private baseGain: GainNode | null = null;
  private harmonic2Gain: GainNode | null = null;
  private harmonic3Gain: GainNode | null = null;
  private lfoGain: GainNode | null = null;

  // Filters
  private filter: BiquadFilterNode | null = null;

  constructor(ctx: AudioContext, output: GainNode) {
    this.ctx = ctx;
    this.output = output;
  }

  start(): void {
    if (this.isPlaying) return;

    const now = this.ctx.currentTime;

    // Create nodes
    this.masterGain = this.ctx.createGain();
    this.baseGain = this.ctx.createGain();
    this.harmonic2Gain = this.ctx.createGain();
    this.harmonic3Gain = this.ctx.createGain();
    this.lfoGain = this.ctx.createGain();
    this.filter = this.ctx.createBiquadFilter();

    // Base oscillator - 55Hz (A1) low rumble
    this.baseOsc = this.ctx.createOscillator();
    this.baseOsc.type = 'sine';
    this.baseOsc.frequency.setValueAtTime(55, now);

    // Harmonic 2 - octave up with slight detuning
    this.harmonic2 = this.ctx.createOscillator();
    this.harmonic2.type = 'sine';
    this.harmonic2.frequency.setValueAtTime(110.5, now); // Slight detune

    // Harmonic 3 - fifth
    this.harmonic3 = this.ctx.createOscillator();
    this.harmonic3.type = 'triangle';
    this.harmonic3.frequency.setValueAtTime(82.5, now); // E2 (fifth below A2)

    // LFO for modulation
    this.lfoOsc = this.ctx.createOscillator();
    this.lfoOsc.type = 'sine';
    this.lfoOsc.frequency.setValueAtTime(0.5, now);

    // Filter setup
    this.filter.type = 'lowpass';
    this.filter.frequency.setValueAtTime(200, now);
    this.filter.Q.setValueAtTime(1, now);

    // Connect graph
    this.baseOsc.connect(this.baseGain);
    this.harmonic2.connect(this.harmonic2Gain);
    this.harmonic3.connect(this.harmonic3Gain);

    this.baseGain.connect(this.filter);
    this.harmonic2Gain.connect(this.filter);
    this.harmonic3Gain.connect(this.filter);

    this.filter.connect(this.masterGain);
    this.masterGain.connect(this.output);

    // LFO to master gain
    this.lfoOsc.connect(this.lfoGain);
    this.lfoGain.connect(this.masterGain.gain);

    // Initial levels (low intensity)
    this.masterGain.gain.setValueAtTime(0, now);
    this.masterGain.gain.linearRampToValueAtTime(0.15, now + 0.5);
    this.baseGain.gain.setValueAtTime(0.5, now);
    this.harmonic2Gain.gain.setValueAtTime(0, now);
    this.harmonic3Gain.gain.setValueAtTime(0, now);
    this.lfoGain.gain.setValueAtTime(0, now);

    // Start oscillators
    this.baseOsc.start(now);
    this.harmonic2.start(now);
    this.harmonic3.start(now);
    this.lfoOsc.start(now);

    this.isPlaying = true;
  }

  stop(): void {
    if (!this.isPlaying) return;

    const now = this.ctx.currentTime;

    // Fade out
    if (this.masterGain) {
      this.masterGain.gain.linearRampToValueAtTime(0, now + 0.3);
    }

    // Stop oscillators after fade
    setTimeout(() => {
      this.baseOsc?.stop();
      this.harmonic2?.stop();
      this.harmonic3?.stop();
      this.lfoOsc?.stop();

      this.baseOsc = null;
      this.harmonic2 = null;
      this.harmonic3 = null;
      this.lfoOsc = null;
      this.masterGain = null;
      this.baseGain = null;
      this.harmonic2Gain = null;
      this.harmonic3Gain = null;
      this.lfoGain = null;
      this.filter = null;
    }, 400);

    this.isPlaying = false;
  }

  setIntensity(intensity: number): void {
    if (!this.isPlaying) return;

    // Clamp intensity 0-1
    intensity = Math.max(0, Math.min(1, intensity));

    const now = this.ctx.currentTime;
    const transitionTime = 0.1;

    // Dynamic audio response based on intensity
    // 0-30%: Low rumble only
    // 30-60%: Add harmonics, slight detuning
    // 60-80%: Filter opens, LFO starts
    // 80-100%: Full content, rapid pulsing

    // Base volume increases with intensity
    const baseVol = 0.3 + intensity * 0.3;
    this.baseGain?.gain.linearRampToValueAtTime(baseVol, now + transitionTime);

    // Harmonics fade in after 30%
    const harmonic2Vol = intensity > 0.3 ? (intensity - 0.3) * 0.4 : 0;
    const harmonic3Vol = intensity > 0.3 ? (intensity - 0.3) * 0.3 : 0;
    this.harmonic2Gain?.gain.linearRampToValueAtTime(harmonic2Vol, now + transitionTime);
    this.harmonic3Gain?.gain.linearRampToValueAtTime(harmonic3Vol, now + transitionTime);

    // Filter opens with intensity
    const filterFreq = 200 + intensity * 800;
    this.filter?.frequency.linearRampToValueAtTime(filterFreq, now + transitionTime);

    // LFO starts at 60%, gets faster with intensity
    if (intensity > 0.6 && this.lfoOsc && this.lfoGain) {
      const lfoSpeed = 0.5 + (intensity - 0.6) * 8; // 0.5 to 3.5 Hz
      const lfoDepth = (intensity - 0.6) * 0.15;
      this.lfoOsc.frequency.linearRampToValueAtTime(lfoSpeed, now + transitionTime);
      this.lfoGain.gain.linearRampToValueAtTime(lfoDepth, now + transitionTime);
    } else {
      this.lfoGain?.gain.linearRampToValueAtTime(0, now + transitionTime);
    }

    // Master volume slight increase at high intensity
    const masterVol = 0.15 + intensity * 0.1;
    this.masterGain?.gain.linearRampToValueAtTime(masterVol, now + transitionTime);

    // Detune harmonics more at high intensity
    if (this.harmonic2) {
      const detune = 0.5 + intensity * 2; // 0.5 to 2.5 Hz detune
      this.harmonic2.frequency.linearRampToValueAtTime(110 + detune, now + transitionTime);
    }
  }
}
